import { type Tranche } from "@lib/tranche";
import sum from "lodash/sum";
import type { Funder, IncrementalEvalStep } from "./aggregate";
import { HOLD_AMOUNT } from "@lib/constants";

export const incrementalEvalsRemoveAmountEpsilon = 1.0;

export function trancheListFromIncrementalEvalSteps(
  funder: Funder,
  holdProposalId: string,
): Tranche[] {
  const tranches: Tranche[] = [];
  for (const { evaluableId, incrementAmount } of funder.incrementalEvalSteps) {
    if (incrementAmount > 0) {
      let currentTranche = tranches[tranches.length - 1];
      if (!currentTranche || !(evaluableId in currentTranche.allocations)) {
        currentTranche = {
          height: 0.01,
          allocations: {
            [evaluableId]: {
              amount: 0,
              bottomPlaced: true,
            },
          },
        };
        tranches.push(currentTranche);
      }

      const currentAllocation = currentTranche.allocations[evaluableId];
      if (currentAllocation == null) {
        currentTranche.allocations[evaluableId] = {
          amount: incrementAmount,
          bottomPlaced: true,
        };
      } else {
        currentAllocation.amount += incrementAmount;
      }
    } else {
      const trancheIndicesToRemove: number[] = [];
      let amountRemaining = Math.abs(incrementAmount);
      for (let i = tranches.length - 1; i >= 0; i--) {
        if (amountRemaining <= 0) {
          break;
        }

        const tranche = tranches[i];
        if (tranche == null) {
          continue;
        }
        if (!(evaluableId in tranche.allocations)) {
          continue;
        }

        const allocation = tranche.allocations[evaluableId];
        if (allocation == null) {
          continue;
        }
        if (allocation.amount > amountRemaining) {
          allocation.amount -= amountRemaining;
          amountRemaining = 0;
          break;
        } else {
          amountRemaining -= allocation.amount;
          allocation.amount = 0;
          trancheIndicesToRemove.push(i);
        }
      }

      if (amountRemaining > incrementalEvalsRemoveAmountEpsilon) {
        throw new Error(
          `Could not remove all ${incrementAmount}! ${amountRemaining} remaining.`,
        );
      }

      for (const i of trancheIndicesToRemove) {
        tranches.splice(i, 1);
      }
    }
  }

  const totalHeight = sum(tranches.map((t) => t.height));
  tranches.push({
    height: 5 - totalHeight,
    allocations: {},
  });

  tranches.push({
    height: 0,
    allocations: {
      [holdProposalId]: {
        amount: HOLD_AMOUNT,
        bottomPlaced: true,
      },
    },
  });

  return tranches;
}

export function getFunderBudgetFromIncrementalEvalSteps(
  incrementalEvalSteps: IncrementalEvalStep[],
  holdProposalId: string,
): number {
  let budget = 0;

  for (const step of incrementalEvalSteps) {
    // currently this is not possible in the UI
    if (step.evaluableId === holdProposalId) {
      continue;
    }

    budget += step.incrementAmount;
  }

  return budget;
}
